import { useState, useEffect } from 'react';
import {
  triggerWatermarkCourseAttachments,
  doesWatermarkedAttachmentExist,
  loadAttachments,
} from '../api';
import { WATERMARK_TIMEOUT, WATERMARK_POLLING_INTERVAL } from '../constants';
import Waffle from 'edrolo/utils/Waffle';

// status constants, only used internally
const WATERMARK_STATUS = {
  IDLE: 'IDLE',
  ATTACHMENT_LOADED: 'ATTACHMENT_LOADED',
  WATERMARK_NEEDED: 'WATERMARK_NEEDED',
  WATERMARK_TRIGGERED: 'WATERMARK_TRIGGERED',
  READY: 'READY',
  FAILED: 'FAILED',
};

// Queries the branch attachment endpoint for a specific class and its course,
// i.e. /api/v1/classes/:classId/attachments/branches/:courseId/
// This endpoint returns a payload with the ``watermarked_url`` for the user.
// This url is queried with HEAD via doesWatermarkedAttachmentExist.
// If the file is not found, the watermarking endpoint is called.
// At this point, none of this should be indicated to the user.
// When ``isUrlRequested`` is set to true, the hook starts polling the
// ``watermarked_url`` again until the file is found.
//
// returns {
//   url: string,
//   isUrlRequested: bool,
//   setIsUrlRequested: (value: bool) => void
// }
//   - url MAY be null, until setIsUrlRequested(true) is called, AND the
//     watermarked PDF has been generated.
//   - if url is not null, and isUrlRequested is true, then the user definitely
//     wants to download the pdf.
//   - it is recommended to setIsUrlRequested(false) after the user has been
//     navigated to the pdf, that way isUrlRequested can be used for a loader.
export const useWatermarkedCourseAttachment = (
  classId,
  courseId,
  attachmentId
) => {
  const [state, setState] = useState({
    isUrlRequested: false,
    status: WATERMARK_STATUS.IDLE,
    attachment: null,
    watermarkTriggeredAt: 0,
    pollingCount: 0,
  });
  const setIsUrlRequested = (isUrlRequested) => {
    setState({ ...state, isUrlRequested });
  };

  useEffect(() => {
    // reset if user navigates to a different course
    if (state.status !== WATERMARK_STATUS.IDLE) {
      setState({ ...state, status: WATERMARK_STATUS.IDLE });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [courseId, attachmentId]);

  useEffect(() => {
    let timer = null;

    switch (state.status) {
      case WATERMARK_STATUS.IDLE:
        performLoadAttachment(state, setState, classId, courseId, attachmentId);
        break;
      case WATERMARK_STATUS.ATTACHMENT_LOADED:
        performWatermarkCheck(state, setState);
        break;
      case WATERMARK_STATUS.WATERMARK_NEEDED:
        if (
          Waffle.switch_is_active(
            'generate_watermarked_pdfs_before_user_requests_download'
          ) ||
          state.isUrlRequested
        ) {
          performTriggerWatermarking(state, setState, classId, courseId);
        }
        break;
      case WATERMARK_STATUS.WATERMARK_TRIGGERED:
        if (state.isUrlRequested) {
          timer = pollForWatermarkedPDF(state, setState);
        }
        break;
      default:
    }

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [state.status, state.isUrlRequested, state.pollingCount]);

  return {
    url:
      state.status === WATERMARK_STATUS.READY
        ? state.attachment.get('watermarked_url')
        : null,
    // WATERMARK_STATUS.FAILED is handled silently
    isUrlRequested:
      state.status !== WATERMARK_STATUS.FAILED && state.isUrlRequested,
    setIsUrlRequested,
    error: state.status === WATERMARK_STATUS.FAILED,
  };
};

async function performLoadAttachment(
  state,
  setState,
  classId,
  courseId,
  attachmentId
) {
  // load attachment
  const resp = await loadAttachments(classId, 'branches', courseId);
  if (!resp.ok) {
    setState({ ...state, status: WATERMARK_STATUS.FAILED });
    return;
  }

  const watermarkableAttachment = resp.body.find(
    (att) => att.get('requires_watermark') && att.get('id') === attachmentId
  );
  if (!watermarkableAttachment) {
    setState({ ...state, status: WATERMARK_STATUS.FAILED });
    return;
  }
  setState({
    ...state,
    attachment: watermarkableAttachment,
    status: WATERMARK_STATUS.ATTACHMENT_LOADED,
  });
}

async function performWatermarkCheck(state, setState) {
  // trigger watermarking if needed
  const doesExist = await doesWatermarkedAttachmentExist(
    state.attachment.get('watermarked_url')
  );

  if (doesExist) {
    setState({ ...state, status: WATERMARK_STATUS.READY });
  } else {
    setState({ ...state, status: WATERMARK_STATUS.WATERMARK_NEEDED });
  }
}

async function performTriggerWatermarking(state, setState, classId, courseId) {
  const resp = await triggerWatermarkCourseAttachments(classId, courseId);
  if (!resp.ok) {
    setState({ ...state, status: WATERMARK_STATUS.FAILED });
    return;
  }
  setState({
    ...state,
    status: WATERMARK_STATUS.WATERMARK_TRIGGERED,
    watermarkTriggeredAt: new Date().valueOf(),
  });
}

async function pollForWatermarkedPDF(state, setState) {
  if (new Date().valueOf() > state.watermarkTriggeredAt + WATERMARK_TIMEOUT) {
    // timeout
    setState({ ...state, status: WATERMARK_STATUS.FAILED });
    return;
  }
  const doesExist = await doesWatermarkedAttachmentExist(
    state.attachment.get('watermarked_url')
  );
  if (doesExist) {
    setState({ ...state, status: WATERMARK_STATUS.READY });
    return null;
  } else {
    return setTimeout(() => {
      // iterating the polling count will cause the effect to be run again
      setState({ ...state, pollingCount: state.pollingCount + 1 });
    }, WATERMARK_POLLING_INTERVAL);
  }
}
